import React                           from 'react';
import View                            from '@widesk-ui/components/View';
import Segmented                       from '@widesk-ui/Segmented';
import useForceUpdate                  from '@widesk-core/hooks/useForceUpdate';
import { observer }                    from 'mobx-react-lite';
import { DrawTriggerOptions }          from '@tokamap/core';
import { Shape }                       from '@tokamap/core';
import { lineWallThicknessMeters }     from '@tokamap/core';
import { DEFAULT_VOLUME_OPACITY }      from '@tokamap/core';
import Field                           from '@/TokamapEditor/ux/AreaSidebar/Field';
import SliderInputNumber               from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import currentEditorStore              from '@/TokamapEditor/stores/currentEditorStore';
import themeStore                      from '@/TokamapEditor/stores/themeStore';
import { themeValueLock }              from '@/TokamapEditor/helpers/themeImposed';
import { themedStyle }                 from '@/TokamapEditor/helpers/themeImposed';
import type { ThemeLock }              from '@/TokamapEditor/helpers/themeImposed';
import useDesignTokens                 from '@/hooks/useDesignTokens';
import HelpTip                         from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import SwitchField                     from '@/TokamapEditor/ux/SwitchField';
import ToolPreview                     from '@/TokamapEditor/ux/ToolPreview';
import { DemoMark }                    from '@/TokamapEditor/ux/ToolPreview';
import { formatLength }                from '@/TokamapEditor/helpers/mapKind';
import { HEIGHT_RANGES }               from '@/TokamapEditor/helpers/mapKind';
import { metersToUnit }                from '@/TokamapEditor/helpers/mapKind';
import { unitToMeters }                from '@/TokamapEditor/helpers/mapKind';
import SettingsButton                  from '@/TokamapEditor/ux/AreaSidebar/SettingsPopover';
import { BorderOutlined }              from '@ant-design/icons';
import { ColumnHeightOutlined }        from '@ant-design/icons';
import { VerticalAlignBottomOutlined } from '@ant-design/icons';

const LIVE: DrawTriggerOptions = { skipHistory: true, skipSave: true, skipConfirm: true };

const DUGGABLE_KINDS: Shape['shapeKind'][] = ['polygon', 'rectangle', 'circle'];
const DEFAULT_HEIGHT_METERS = 3;
const DEFAULT_DEPTH_METERS = 4;

type Relief = 'flat' | 'volume' | 'dug';

const themeOf = (shape: Shape) => (shape.themeId ? themeStore.coreThemes[shape.themeId] : undefined);

const RELIEF_LABELS: Record<Relief, string> = {
	flat: 'À plat',
	volume: 'En volume',
	dug: 'Creusé',
};

const reliefOf = (shape: Shape): Relief => (shape.depth ? 'dug' : (themeOf(shape)?.height ?? shape.height) ? 'volume' : 'flat');

/**
 * Hauteur et hauteur au-dessus du sol, au repos ou au survol (`hover`). Comme pour la bordure, le
 * survol n'affiche que ce qui y est réglé : à zéro, il reprend la valeur du repos.
 */
const ThreeDOptions = observer(({ hover }: { hover?: boolean; }) => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;

	const lock = themeValueLock(shapes);
	const restHeight = (shape: Shape) => themeOf(shape)?.height ?? shape.height;
	const hoverHeight = (shape: Shape) => themeOf(shape)?.hoverHeight ?? shape.hoverHeight;
	const height = (shape: Shape) => (hover ? hoverHeight(shape) : restHeight(shape));
	const elevation = (shape: Shape) => (hover
		? themeOf(shape)?.hoverElevation ?? shape.hoverElevation
		: themeOf(shape)?.elevation ?? shape.elevation);
	// Le volume tel qu'il s'affiche au survol, qui décide si décoller du sol a un sens.
	const effectiveHeight = (shape: Shape) => (hover ? hoverHeight(shape) ?? restHeight(shape) : restHeight(shape));

	const raisable = shapes.filter(shape => reliefOf(shape) !== 'dug');
	const volumeShapes = hover ? raisable : shapes.filter(shape => reliefOf(shape) === 'volume');
	const elevationShapes = volumeShapes.filter(shape => !!effectiveHeight(shape));
	const dugShapes = hover ? [] : shapes.filter(shape => reliefOf(shape) === 'dug');

	const lines = shapes.every(shape => shape.shapeKind === 'line');
	const toModel = (meters: number) => (meters > 0 ? meters : undefined);

	return (
		<>
			{!hover && (
				<ReliefField
					shapes={shapes}
					lock={lock('height')}
					canBeDug={shapes.every(shape => DUGGABLE_KINDS.includes(shape.shapeKind))}
				/>
			)}

			{!!volumeShapes.length && (
				<MetersField
					label="Hauteur"
					help={hover
						? 'Hauteur au survol, visible quand la carte est inclinée. À zéro, celle de l\'état normal.'
						: lines
							? 'Élève la ligne en mur quand la carte est inclinée.'
							: 'Élève la forme en volume quand la carte est inclinée.'}
					shapes={volumeShapes}
					lock={lock(hover ? 'hoverHeight' : 'height')}
					read={height}
					apply={(ids, meters, options) => editor[hover ? 'setShapeHoverHeight' : 'setShapeHeight'](ids, toModel(meters), options)}
					note={lines && volumeShapes.some(shape => (effectiveHeight(shape) ?? 0) > 0) ? <WallThicknessNote shapes={volumeShapes} /> : undefined}
				/>
			)}

			<MetersField
				label="Hauteur au-dessus du sol"
				help={hover
					? 'Décolle le volume du sol au survol. À zéro, comme à l\'état normal.'
					: 'Décolle le volume du sol quand la carte est inclinée. Sa hauteur reste son épaisseur.'}
				shapes={elevationShapes}
				lock={lock(hover ? 'hoverElevation' : 'elevation')}
				read={elevation}
				apply={(ids, meters, options) => editor[hover ? 'setShapeHoverElevation' : 'setShapeElevation'](ids, toModel(meters), options)}
			/>

			<MetersField
				label="Profondeur"
				help="Profondeur de la zone creusée dans le sol, visible quand la carte est inclinée. Son fond prend la couleur de la forme."
				shapes={dugShapes}
				read={shape => shape.depth}
				apply={(ids, meters, options) => editor.setShapeDepth(ids, toModel(meters), options)}
			/>

			{!!dugShapes.length && (
				<SwitchField
					label={<>En eau<HelpTip>Une surface d'eau translucide un peu sous le bord. Ce qu'on pose dans la zone flotte à la surface au lieu de descendre au fond.</HelpTip></>}
					value={dugShapes.every(shape => shape.water)}
					mixed={new Set(dugShapes.map(shape => !!shape.water)).size > 1}
					onChange={checked => editor.setShapeWater(dugShapes.map(shape => shape.id), checked || undefined)}
				/>
			)}

			{!hover && <VolumeOpacityField shapes={elevationShapes} lock={lock('volumeOpacity')} />}
		</>
	);
});

const ReliefButton = observer(({ hover }: { hover?: boolean; }) => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const tokens = useDesignTokens();
	const forceUpdate = useForceUpdate();

	React.useEffect(() => {
		return editor.mapAdapter.on(event => {
			if (event.type === 'allow-pitch:changed') forceUpdate();
		});
	}, [editor]);

	if (!shapes.length || !editor.getAllowPitch()) return null;
	if (shapes.some(shape => shape.shapeKind === 'image')) return null;

	const unit = currentEditorStore.lengthUnit;
	const reliefs = new Set(shapes.map(reliefOf));
	const relief = reliefs.size === 1 ? [...reliefs][0]! : undefined;
	const measure = (shape: Shape) => (relief === 'dug'
		? shape.depth
		: hover ? themeOf(shape)?.hoverHeight ?? shape.hoverHeight : themeOf(shape)?.height ?? shape.height);
	const measures = new Set(shapes.map(measure));
	const mixed = !relief || measures.size > 1;
	const amount = [...measures][0];

	const summary = mixed
		? 'Plusieurs valeurs'
		: hover && relief !== 'dug'
			? (amount ? `${formatLength(amount, unit)} au survol` : 'Comme au repos')
			: [RELIEF_LABELS[relief], relief !== 'flat' && amount && formatLength(amount, unit)].filter(Boolean).join(' · ');

	const elevations = new Set(shapes.filter(shape => reliefOf(shape) !== 'dug').map(shape => (hover
		? themeOf(shape)?.hoverElevation ?? shape.hoverElevation
		: themeOf(shape)?.elevation ?? shape.elevation) ?? 0));
	const elevation = [...elevations][0];
	const opacities = new Set(shapes.filter(shape => reliefOf(shape) === 'volume').map(shape => themeOf(shape)?.volumeOpacity ?? shape.volumeOpacity ?? DEFAULT_VOLUME_OPACITY));
	const opacity = [...opacities][0];
	const waters = new Set(shapes.filter(shape => reliefOf(shape) === 'dug').map(shape => !!shape.water));

	const details = [
		elevations.size > 1 ? 'hauteurs au-dessus du sol différentes' : elevation ? `à ${formatLength(elevation, unit)} du sol` : undefined,
		hover ? undefined : opacities.size > 1 ? 'opacités des murs différentes' : opacity !== undefined && opacity !== DEFAULT_VOLUME_OPACITY ? `murs à ${Math.round(opacity * 100)} %` : undefined,
		hover ? undefined : waters.size > 1 ? 'en eau pour une partie' : waters.has(true) ? 'en eau' : undefined,
	].filter((detail): detail is string => !!detail);

	return (
		<SettingsButton
			label="Relief"
			title={<>Relief<HelpTip>Visible quand la carte est inclinée. En volume : la forme s'élève. Creusé : elle s'enfonce dans le sol, comme une cour en contrebas ou un patio.</HelpTip></>}
			summary={summary}
			details={details}
			mixed={mixed}
			width={300}
			preview={<ReliefIcon relief={relief} color={tokens.text.secondary} />}
		>
			<ThreeDOptions hover={hover} />
		</SettingsButton>
	);
});

function ReliefIcon({ relief, color }: { relief?: Relief; color: string; }) {
	const Icon = relief === 'dug' ? VerticalAlignBottomOutlined : relief === 'volume' ? ColumnHeightOutlined : BorderOutlined;

	return <Icon style={{ fontSize: 22, color, flexShrink: 0 }} />;
}

const ReliefField = observer(({ shapes, lock, canBeDug }: {
	shapes: Shape[];
	lock?: ThemeLock<unknown>;
	canBeDug: boolean;
}) => {
	const editor = currentEditorStore.get;
	const reliefs = new Set(shapes.map(reliefOf));
	const ids = shapes.map(shape => shape.id);

	const choose = (relief: Relief) => {
		if (relief === 'volume') editor.setShapeRelief(ids, { height: DEFAULT_HEIGHT_METERS });
		else if (relief === 'dug') editor.setShapeRelief(ids, { depth: DEFAULT_DEPTH_METERS });
		else editor.setShapeRelief(ids, {});
	};

	return (
		<Field lockedBy={lock} label="Type">
			<Segmented
				block
				size="small"
				value={reliefs.size === 1 ? [...reliefs][0] : undefined}
				onChange={value => choose(value as Relief)}
				options={[
					{ value: 'flat', label: 'À plat' },
					{ value: 'volume', label: <ToolPreview name="reliefVolume" label="En volume" placement="right">En volume<DemoMark /></ToolPreview> },
					...(canBeDug ? [{ value: 'dug', label: <ToolPreview name="reliefDug" label="Creusé" placement="right">Creusé<DemoMark /></ToolPreview> }] : []),
				]}
			/>
		</Field>
	);
});

const VolumeOpacityField = ({ shapes, lock }: { shapes: Shape[]; lock?: ThemeLock<unknown>; }) => {
	const editor = currentEditorStore.get;
	const tokens = useDesignTokens();

	if (!shapes.length) return null;

	const percent = (shape: Shape) => Math.round((themeOf(shape)?.volumeOpacity ?? shape.volumeOpacity ?? DEFAULT_VOLUME_OPACITY) * 100);
	const mixed = new Set(shapes.map(percent)).size > 1;

	const save = (input: number | undefined, options?: DrawTriggerOptions) => {
		editor.setShapeVolumeOpacity(shapes.map(shape => shape.id), Number(input ?? 0) / 100, options);
	};

	return (
		<Field lockedBy={lock} label={<>Opacité des murs<HelpTip>Laisse voir à travers le volume quand la carte est inclinée, toit compris.</HelpTip></>}>
			<View gap={4}>
				<SliderInputNumber
					value={percent(shapes[0]!)}
					min={0}
					max={100}
					step={10}
					suffix="%"
					onChange={input => save(input, LIVE)}
					onAfterChange={input => save(input)}
				/>

				{mixed && (
					<View size={12} color={tokens.text.muted}>
						Valeurs différentes
					</View>
				)}
			</View>
		</Field>
	);
};

/** Le mur prend l'épaisseur du trait quand elle est en mètres : une épaisseur en pixels ne dit rien de sa taille au sol. */
const WallThicknessNote = observer(({ shapes }: { shapes: Shape[]; }) => {
	const styles = shapes.map(themedStyle);
	const thicknesses = new Set(styles.map(lineWallThicknessMeters));
	const fromStroke = styles.every(style => style?.borderWidthUnit === 'm');

	const unit = currentEditorStore.lengthUnit;

	if (thicknesses.size > 1) return <>Épaisseur du mur : celle du trait, ou {formatLength(0.2, unit)} pour un trait en pixels.</>;

	const thickness = formatLength([...thicknesses][0]!, unit);

	return fromStroke
		? <>Épaisseur du mur : {thickness}, celle du trait.</>
		: <>Épaisseur du mur : {thickness}. Réglez l'épaisseur du trait en {unit} pour la choisir.</>;
});

const MetersField = observer(({ label, help, shapes, lock, read, apply, note }: {
	label: string;
	lock?: ThemeLock<unknown>;
	help: string;
	note?: React.ReactNode;
	shapes: Shape[];
	read: (shape: Shape) => number | undefined;
	apply: (shapeIds: string[], meters: number, options?: DrawTriggerOptions) => void;
}) => {
	const tokens = useDesignTokens();
	const unit = currentEditorStore.lengthUnit;
	const range = HEIGHT_RANGES[unit];

	if (!shapes.length) return null;

	const mixed = new Set(shapes.map(read)).size > 1;
	const value = metersToUnit(read(shapes[0]!) ?? 0, unit);

	const save = (input: number | undefined, options?: DrawTriggerOptions) => {
		const meters = unitToMeters(Number(input), unit);
		apply(shapes.map(shape => shape.id), meters > 0 ? meters : 0, options);
	};

	return (
		<Field lockedBy={lock} label={<>{label}<HelpTip>{help}</HelpTip></>}>
			<View gap={4}>
				<SliderInputNumber
					value={value}
					min={0}
					max={range.sliderMax}
					inputMax={range.inputMax}
					step={range.step}
					suffix={unit}
					onChange={input => save(input, LIVE)}
					onAfterChange={input => save(input)}
				/>

				{mixed && (
					<View size={12} color={tokens.text.muted}>
						Valeurs différentes
					</View>
				)}

				{!!note && <View size={12} color={tokens.text.muted}>{note}</View>}
			</View>
		</Field>
	);
});

export default ReliefButton;
